import * as React from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { Tooltip, CircularProgress } from '@mui/material';

/* TYPES */
import {
  useRemoveCoreMutation,
  useRemoveHmmMutation,
  useRemoveBngMutation,
  useRemoveConductorMutation,
  useInitializeSourceMutation,
  useSourceStatusQuery,
  SourceStatusQuery,
} from '../../../types/generated-types';

/* SHARED COMPONENTS */
import { Notifier } from '../../system/services/notificationManager';

/* MUI */
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
  Theme,
  Box,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  useIsMediumScreen,
  useIsSmallScreen,
} from '../../system/SystemMediaQueriesProvider';

/* ICONS */
import SyncIcon from '@mui/icons-material/SyncTwoTone';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';

/* OTHER */
import {
  MobileListCard,
  MobileListDataTableProps,
} from '../shared/mobile-list-card';
import {
  BottomMenuItems,
  useInjectableComponents,
} from '../../system/services/injectableComponentsManager';
import { Add } from '@mui/icons-material';
import { useCallback, useEffect } from 'react';
import { useSystemConnection } from '../../system/ConnectionManager';

import { ManagedSource, useSources } from '../../system/EntityManager';
import DataTable from '../shared/data-table';
import {
  GridColDef,
  GridRowParams,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import { Capacitor } from '@capacitor/core';
import { buildSourcesCsv, saveCsvBlob } from './source-csv';

/** Interval in ms for refetching source list data from the server. Default: 1 minute. */
const SOURCE_LIST_REFETCH_INTERVAL_MS = 60000;

type SourceStatus = 'Connected' | 'Stale' | 'Offline' | 'Uninitialized';
type SourceRow = ManagedSource & {
  id: string;
  lastHeartbeat: string;
  status: SourceStatus;
  buttonColor: string;
};

interface RemoveSourceDialogProps {
  open: boolean;
  handleClose: () => void;
  removeSource: () => void;
}

function buildSourceTimestampMap(sourceStatusData?: SourceStatusQuery) {
  const timestampMap: Record<string, number> = {};

  sourceStatusData?.cores?.forEach((core) => {
    if (core?._id && core.timestamp) {
      timestampMap[core._id] = core.timestamp;
    }
  });
  sourceStatusData?.hMMs?.forEach((hmm) => {
    if (hmm?._id && hmm.timestamp) {
      timestampMap[hmm._id] = hmm.timestamp;
    }
  });
  sourceStatusData?.bNGs?.forEach((bng) => {
    if (bng?._id && bng.timestamp) {
      timestampMap[bng._id] = bng.timestamp;
    }
  });
  sourceStatusData?.conductors?.forEach((conductor) => {
    if (conductor?._id && conductor.timestamp) {
      timestampMap[conductor._id] = conductor.timestamp;
    }
  });

  return timestampMap;
}

function formatData(
  data: ManagedSource[],
  sourceTimestampMap: Record<string, number>,
  theme: Theme,
) {
  const myFormattedData: SourceRow[] = [];
  if (data && data.length) {
    data
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach((row) => {
        const timestamp = sourceTimestampMap[row._id];
        const lastHeartbeat = timestamp
          ? moment(timestamp).format('MMM D, YYYY h:mm:ss A')
          : 'No heartbeats received';

        const url: string =
          row.url ??
          (row.__typename === 'BNG' ? row.localUrl : undefined) ??
          'No URL Available';
        let status: SourceStatus = 'Uninitialized';
        let buttonColor = theme.palette.primary.main;
        if (lastHeartbeat !== 'No heartbeats received') {
          const timeDiff = Date.now() - new Date(lastHeartbeat).valueOf();
          if (timeDiff < 75000) {
            status = 'Connected';
            buttonColor = theme.palette.secondary.main;
          } else if (timeDiff < 300000) {
            status = 'Stale';
            buttonColor = theme.palette.warning.main;
          } else {
            status = 'Offline';
            buttonColor = theme.palette.error.main;
          }
        }
        myFormattedData.push({
          ...row,
          id: row._id,
          url,
          lastHeartbeat,
          status,
          buttonColor,
        });
      });
  }
  return myFormattedData;
}

const RemoveSourceDialog = (props: RemoveSourceDialogProps) => {
  const { open, handleClose, removeSource } = props;
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">{'Remove Source'}</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          Are you sure? this action cannot be reversed
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => {
            removeSource();
            handleClose();
          }}
          autoFocus
        >
          Remove
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export function SourceList() {
  const [open, setOpen] = React.useState(false);

  const navigate = useNavigate();
  const currentLocation = useLocation();
  const [filterParams] = useSearchParams();
  const { pathname } = currentLocation;
  const theme = useTheme();

  const [sourceTimestampMap, setSourceTimestampMap] = React.useState<
    Record<string, number>
  >({});
  const [statusRefreshTick, setStatusRefreshTick] = React.useState(0);

  const { sources, error, loading } = useSources();

  const {
    data: sourceStatusData,
    error: statusLoadingError,
    loading: statusLoading,
    refetch,
  } = useSourceStatusQuery();

  useEffect(() => {
    if (statusLoading) return;
    if (statusLoadingError) {
      setSourceTimestampMap({});
      return;
    }

    setSourceTimestampMap(buildSourceTimestampMap(sourceStatusData));
  }, [sourceStatusData, statusLoading, statusLoadingError]);

  useEffect(() => {
    const refreshSourceStatuses = async () => {
      try {
        const result = await refetch();
        setSourceTimestampMap(buildSourceTimestampMap(result.data));
      } catch {
        // Retain the last heartbeat timestamps so their statuses can still age.
      } finally {
        setStatusRefreshTick((prev) => prev + 1);
      }
    };

    const intervalId = window.setInterval(() => {
      void refreshSourceStatuses();
    }, SOURCE_LIST_REFETCH_INTERVAL_MS);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [refetch]);

  useEffect(() => {
    if (error) {
      Notifier.error('Error loading sources. Please try refreshing the page');
      console.error(error);
    }
  }, [error]);

  const [formattedData, setFormatedData] = React.useState<SourceRow[]>([]);

  useEffect(() => {
    if (sources) {
      const type = filterParams.get('type') ?? null;
      const term = filterParams.get('term') ?? null;

      let typeKey: string | null = null;
      if (type) {
        switch (type.toLowerCase()) {
          case 'core':
            typeKey = 'Core';
            break;
          case 'bng':
            typeKey = 'BNG';
            break;
          case 'hmm':
            typeKey = 'HMM';
            break;
          case 'conductor':
            typeKey = 'Conductor';
            break;
          default:
            typeKey = null;
        }
      }
      const filteredSources = sources.filter((source) => {
        const typeRes = type && typeKey ? source.type === typeKey : true;
        const searchRes = term ? source.name.includes(term) : true;
        return typeRes && searchRes;
      });

      const myFormattedData: SourceRow[] =
        filteredSources && filteredSources.length
          ? formatData(filteredSources, sourceTimestampMap, theme)
          : [];

      setFormatedData(myFormattedData);
    } else {
      setFormatedData([]);
    }
  }, [sources, sourceTimestampMap, filterParams, statusRefreshTick, theme]);

  const [sourceId] = React.useState<ManagedSource['_id']>('');
  const [sourceType] = React.useState('');
  const showCsvExport = !Capacitor.isNativePlatform();

  const isSmall = useIsSmallScreen();
  const isMedium = useIsMediumScreen();

  const [deleteCore] = useRemoveCoreMutation();
  const [deleteHmm] = useRemoveHmmMutation();
  const [deleteBng] = useRemoveBngMutation();
  const [deleteConductor] = useRemoveConductorMutation();
  const [initializeSource] = useInitializeSourceMutation();

  const handleInitializeSource = useCallback(
    async (sourceId: string, sourceType?: string) => {
      if (!sourceId) {
        return;
      }

      try {
        await initializeSource({
          variables: {
            id: sourceId,
            options:
              sourceType === 'Conductor'
                ? {
                    pushMeta: true,
                    pushHistoricReadings: true,
                    autoDiscovery: true,
                    phase1Simulators: false,
                  }
                : { pushMeta: true, pushHistoricReadings: true },
          },
        });

        Notifier.success('Source initialized');
      } catch (e) {
        Notifier.error('Error initializing Source');
      }
    },
    [initializeSource],
  );

  const handleRemoveSource = async () => {
    switch (sourceType) {
      case 'Core':
        try {
          await deleteCore({ variables: { id: sourceId } });
          Notifier.success('Source deleted');
        } catch (e) {
          Notifier.error('Unable to delete Source');
        }
        break;
      case 'BNG':
        try {
          await deleteBng({ variables: { id: sourceId } });
          Notifier.success('Source deleted');
        } catch (e) {
          Notifier.error('Unable to delete Source');
        }
        break;
      case 'HMM':
        try {
          await deleteHmm({ variables: { id: sourceId } });
          Notifier.success('Source deleted');
        } catch (e) {
          Notifier.error('Unable to delete Source');
        }
        break;
      case 'Conductor':
        try {
          await deleteConductor({ variables: { id: sourceId } });
          Notifier.success('Source deleted');
        } catch (e) {
          Notifier.error('Unable to delete Source');
        }
        break;
      default:
    }
  };

  // Use the same pagination logic as users list - enable pagination when there are more than 50 sources
  const needsPagination = (formattedData?.length ?? 0) > 50;

  const nameColumn = isSmall
    ? ({
        field: 'name',
        headerName: 'Name',
        align: 'center',
        flex: 1,
        width: 250,
        renderCell: (params: GridRenderCellParams<Partial<any>>) => {
          return (
            <MobileListCard cardSx={{ height: '100%' }}>
              <Typography
                component="h5"
                sx={{ fontSize: 18, fontWeight: 800 }}
                color="text.secondary"
              >
                {params.value}
              </Typography>
              <Typography
                component="div"
                color="text.secondary"
                sx={{ fontSize: 16 }}
              >
                <b>Type:</b> {params.row.type}
              </Typography>
              <Typography
                component="div"
                sx={{
                  fontSize: 16,
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                }}
                color="text.secondary"
              >
                <b>URL:</b> {params.row.url}
              </Typography>
              <Typography
                component="div"
                sx={{ fontSize: 16 }}
                color="text.secondary"
              >
                <b>Connection Status:</b>{' '}
                <span
                  style={{
                    backgroundColor: params.row.buttonColor,
                    padding: '2px 6px',
                  }}
                >
                  {params.row.status}
                </span>
              </Typography>
              <Typography
                component="div"
                sx={{ fontSize: 16 }}
                color="text.secondary"
              >
                <b>Last Heartbeat:</b> {params.row.lastHeartbeat}
              </Typography>
              <Button
                onClick={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  await handleInitializeSource(
                    params.row._id ?? '',
                    params.row.type,
                  );
                }}
                fullWidth={true}
                color="secondary"
                size="small"
                variant="contained"
                endIcon={<SyncIcon />}
                aria-label="initialize source"
                sx={{ marginTop: '2px', height: '26px' }}
              >
                Initialize Source
              </Button>
            </MobileListCard>
          );
        },
      } as GridColDef)
    : {
        field: 'name',
        headerName: 'Name',
        flex: 2,
      };

  const columns = [
    { ...nameColumn },
    { field: 'type', headerName: 'Type', flex: 0.5 },
    { field: 'url', headerName: 'URL', flex: 2 },
    {
      field: 'status',
      headerName: 'Connection Status',
      flex: 1,
      renderCell: (params: any) => {
        return (
          <Tooltip title={params.row.lastHeartbeat}>
            <Box
              sx={{
                backgroundColor: params.row.buttonColor,
                height: 'auto',
                width: '80%',
                justifyContent: 'center',
                alignItems: 'center',
                display: 'flex',
                borderRadius: '4px',
                padding: '6px 0',
              }}
            >
              {params.value}
            </Box>
          </Tooltip>
        );
      },
    },
  ];

  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const { setContextMenuItems } = useInjectableComponents();

  const handleRowClick = (params: GridRowParams) => {
    if (params.row?.id) {
      navigate(`${params.row.id}/view-source`);
    }
  };

  function exportCsv() {
    const csv = buildSourcesCsv(formattedData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });

    void saveCsvBlob(blob, 'sources.csv').catch((error) => {
      console.error('Unable to export sources CSV', error);
    });
  }

  const { client } = useSystemConnection();
  useEffect(() => {
    const items: BottomMenuItems = [
      {
        items: [
          {
            id: 'menu-item-add-source',
            label: 'Add Source',
            icon: <Add fontSize="small" />,
            action: () => navigate('/sources/create'),
            permit: {
              action: 'create',
              subject: 'Source',
            },
          },
          {
            id: 'menu-item-refresh-sources',
            label: 'Refresh Source List',
            icon: <Add fontSize="small" />,
            action: async () => {
              await client?.refetchQueries({ include: ['SourceList'] });
              Notifier.success('Sources refreshed');
            },
          },
          {
            id: 'menu-item-remove-source',
            label: 'Delete Source',
            icon: <Add fontSize="small" />,
            action: handleClickOpen,
            permit: {
              action: 'delete',
              subject: 'Source',
            },
          },
        ],
      },
    ];

    setContextMenuItems(items);
    return () => {
      setContextMenuItems(undefined);
    };
  }, [client, navigate, pathname, setContextMenuItems]);

  if (loading) {
    return (
      <Box
        component="div"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        width="100%"
        height="100%"
      >
        <Box sx={{ marginBottom: '2rem' }}>
          <CircularProgress color="secondary" size="3rem" />
        </Box>
        <Box>
          <Typography variant="h4" color="secondary">
            Preparing your data...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        padding: '10px 5px',
        flexDirection: 'column',
        flex: 1,
        alignItems: 'stretch',
        cursor: 'pointer',
        height: needsPagination ? 'calc(100vh - 200px)' : 'auto',
      }}
    >
      <DataTable
        {...(isSmall ? MobileListDataTableProps : {})}
        getRowId={(row: SourceRow) => row.id}
        customToolbarElement={
          showCsvExport ? (
            <Button
              color="secondary"
              disabled={!formattedData.length}
              fullWidth={isSmall}
              size="small"
              startIcon={<DownloadOutlinedIcon />}
              variant="outlined"
              onClick={exportCsv}
              sx={{
                '& .MuiSvgIcon-root': {
                  color: 'inherit',
                },
              }}
            >
              Export CSV
            </Button>
          ) : undefined
        }
        additionalProps={{
          onRowClick: (params: GridRowParams) => handleRowClick(params),
        }}
        columns={columns}
        suppressAutoPageSize={true}
        rowHeight={isSmall ? 175 : undefined}
        columnVisibilityModel={{
          type: !isSmall,
          url: !isSmall && !isMedium,
          text: !isSmall,
          status: !isSmall,
        }}
        rows={formattedData}
        hideFooter={false}
        pageSize={50}
        pageSizeOptions={[10, 25, 50, 100, { label: 'All', value: -1 }]}
        loading={loading}
        // sxProps={{
        //   width: '100%',
        //   height: '100%',
        // }}
        initialState={{
          sorting: {
            sortModel: [{ field: 'name', sort: 'asc' }],
          },
        }}
      />
      <RemoveSourceDialog
        open={open}
        handleClose={() => handleClose()}
        removeSource={() => handleRemoveSource()}
      />
    </div>
  );
}
